import { Injectable, inject, computed, signal, type ResourceRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, of, startWith } from 'rxjs';
import type { IconCategoryGroup, IconResponse } from '@zag/core/models';

/**
 * Service for fetching icons from the catalog API.
 * Uses rxResource for automatic caching and state management.
 */
@Injectable({ providedIn: 'root' })
export class IconsService {
  private readonly http = inject(HttpClient);

  // Trigger signal for manual reloads
  private readonly _loadTrigger = signal(1);

  /**
   * Fetch all icons grouped by category.
   * This is the primary API for the icon picker UI.
   */
  private readonly groupedResource: ResourceRef<IconCategoryGroup[]> = rxResource({
    params: () => ({ trigger: this._loadTrigger() }),
    stream: () =>
      this.http.get<IconCategoryGroup[]>('/api/v1/core/icons/grouped').pipe(
        startWith([]), // Emit empty array immediately to prevent "no value" error
        catchError(error => {
          console.error('Failed to load icons:', error);
          return of([]); // Return empty array on error
        })
      ),
    defaultValue: [],
  });

  /**
   * All icons grouped by category
   */
  readonly groupedIcons = computed(() => this.groupedResource.value() ?? []);

  /**
   * Whether grouped icons are loading
   */
  readonly isLoading = computed(() => this.groupedResource.isLoading());

  /**
   * Error if grouped icons failed to load
   */
  readonly error = computed(() => this.groupedResource.error());

  /**
   * Flat list of all icons (for search)
   */
  readonly allIcons = computed(() => {
    const groups = this.groupedIcons();
    return groups.flatMap((g: IconCategoryGroup) => g.icons);
  });

  /**
   * Find an icon by name
   */
  findByName(name: string): IconResponse | undefined {
    return this.allIcons().find((icon: IconResponse) => icon.name === name);
  }

  /**
   * Reload the icons from the API
   */
  reload(): void {
    this._loadTrigger.update(t => t + 1);
  }
}
